import type {
  CopilotSketch,
  CopilotSketchBlock,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { Color } from "src/elements";

export const OTHER_BRAND = "Other";

const PALETTE: readonly string[] = [
  Color.paletteSkyBlue,
  Color.paletteOrange,
  Color.paletteTeal,
  Color.palettePurple,
  Color.paletteGold,
  Color.paletteRed,
  Color.paletteEmerald,
  Color.paletteIndigo,
];

export const OTHER_COLOR: string = Color.gray400;

/** At most this many brands are named in a sketch's legend. */
export const LEGEND_BRANDS = 8;

export type SketchPalette = (block: Pick<CopilotSketchBlock, "brand" | "color">) => string;

const areaOf = (block: CopilotSketchBlock) =>
  Math.max(0, block.xRight - block.xLeft) * Math.max(0, block.yTop - block.yBottom);

const roomOf = (sketches: readonly CopilotSketch[]) => {
  const room = new Map<string, number>();
  for (const block of sketches.flatMap(({ blocks }) => blocks)) {
    if (block.brand === OTHER_BRAND) continue;
    room.set(block.brand, (room.get(block.brand) ?? 0) + areaOf(block));
  }
  return [...room.entries()].sort(([a, roomA], [b, roomB]) => roomB - roomA || a.localeCompare(b));
};

/**
 * One colour per brand for every sketch in a conversation. The brief sends the
 * mudmap steps' colour for each brand, and that wins; a brand without one gets
 * the palette if it is among the 8 that take the most room, else grey. "Other"
 * is always grey.
 */
export const sketchPaletteOf = (sketches: readonly CopilotSketch[]): SketchPalette => {
  const ranked = roomOf(sketches).slice(0, PALETTE.length);
  const colors = new Map(ranked.map(([brand], index) => [brand, PALETTE[index]]));
  return ({ brand, color }) =>
    brand === OTHER_BRAND ? OTHER_COLOR : (color ?? colors.get(brand) ?? OTHER_COLOR);
};

export interface SketchLegend {
  named: { brand: string; color: string }[];
  /** Brands drawn but not named. */
  more: number;
}

/**
 * The brands a legend names: the most room first, only those whose colour no
 * other brand wears, at most `LEGEND_BRANDS`. Every other brand is counted.
 */
export const sketchLegendOf = (
  sketches: readonly CopilotSketch[],
  palette: SketchPalette,
): SketchLegend => {
  const ranked = roomOf(sketches);
  const colorOf = new Map(
    sketches.flatMap(({ blocks }) => blocks.map((block) => [block.brand, palette(block)] as const)),
  );
  const wearers = new Map<string, number>();
  for (const [brand] of ranked) {
    const color = colorOf.get(brand) ?? OTHER_COLOR;
    wearers.set(color, (wearers.get(color) ?? 0) + 1);
  }
  const named = ranked
    .map(([brand]) => ({ brand, color: colorOf.get(brand) ?? OTHER_COLOR }))
    .filter(({ color }) => color !== OTHER_COLOR && (wearers.get(color) ?? 0) <= 1)
    .slice(0, LEGEND_BRANDS);
  return { named, more: ranked.length - named.length };
};
